import Assistant from "./Assistant";
import Broker from "./Broker";
import CategoryCode from "./CategoryCode";
import Cession from "./Cession";
import CompanySearch from "./CompanySearch";
import ConfiguredPeopleFields from "./ConfiguredPeopleFields";
import Duns from "./Duns";
import FormOfBusiness from "./FormOfBusiness";
import IndustryClass from "./IndustryClass";
import InsuredAddressComponents from "./InsuredAddressComponents";
import InsuredLogo from "./InsuredLogo";
import ModelSelector from "./ModelSelector";
import NjTransactionNumber from "./NjTransactionNumber";
import PersonSelect from "./PersonSelect";
import PolicySummary from "./PolicySummary";
import ProductLine from "./ProductLine";
import ProgramStatus from "./ProgramStatus";
import ProgramTiv from "./ProgramTiv";
import ReinsuranceAggregator from "./ReinsuranceAggregator";
import Renewal from "./Renewal";
import SICCodeSearch from "./SICCodeSearch";
import StandaloneExposure from "./StandaloneExposure";
import StateOfFiling from "./StateOfFiling";
import StatusOverrideSelector from "./StatusOverrideSelector";
import SubmissionTags from "./SubmissionTags";
import SurplusBroker from "./SurplusBroker";
import Tower from "./Tower";
import Links from "./links/Links";
import { makeTransactionOptions } from "./transactionSupport";
import { SearchRounded as SearchIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import * as dates from "common/dates";
import Component from "components/Component";
import AIHighlightWrapper from "components/common/AIHighlightWrapper";
import AISuggestionTextField from "components/common/AISuggestionTextField";
import DataScore from "components/common/DataScore";
import Disabled from "components/common/Disabled";
import DocumentsIssued from "components/common/DocumentsIssued";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import HiddenIfSnapshot from "components/common/HiddenIfSnapshot";
import IconButton from "components/common/IconButton";
import Notes from "components/common/Notes";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import PrimaryExposure from "components/common/PrimaryExposure";
import Toggle from "components/common/Toggle";
import Visible from "components/common/Visible";
import SelfInsuredRetentions from "components/inputs/claims/SelfInsuredRetentions";
import RegulatoryExemptions from "components/inputs/regulatoryExemptions/RegulatoryExemptions";
import ActiveLayerEditor from "components/layers/ActiveLayerEditor";
import ActiveLayerPremiumAdjustmentsEditor from "components/layers/ActiveLayerPremiumAdjustmentsEditor";
import ActiveLayerSelector from "components/layers/ActiveLayerSelector";
import ReinsuranceSelector from "components/layers/ReinsuranceSelector";
import PaymentTermsForm from "components/payments/PaymentTermsForm";
import ProgramPaymentTerms from "components/payments/ProgramPaymentTerms";
import WithResearchBadge from "components/research/WithResearchBadge";
import { makeSearch } from "components/search/SearchTextField";
import Share from "components/transfer/Share";
import * as config from "config";
import * as labels from "config.labels";
import {
  PROGRAM_UNDERWRITER_ASSISTANT,
  INSURED_ADDRESS_COMPONENTS,
  MAILING_ADDRESS,
} from "flags";
import { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { createSelector } from "reselect";
import * as staticDataService from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import {
  updatePreferredUnderwriter,
  updatePreferredUnderwriterName,
  updateSecondaryUnderwriter,
  updateUnderwriterAssistant,
} from "store/actions/people/peopleActions";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as layerDefaultsStore from "store/layerDefaults";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import {
  selectSecondaryUnderwriter,
  selectPreferredUnderwriter,
  selectUnderwriterAssistant,
} from "store/selectors/people/peopleSelectors";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as staticDataSelectors from "store/selectors/temp/staticData/staticDataSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const US = "US";
const NON_US = "NON_US";

// Stable function references for search components
const naicsDisplayResultText = (result) => `(${result._CODE}) ${result._TITLE}`;
const naicsRenderValue = (result) => `(${result.code}) ${result.title}`;

const UnderwriterSelect = ({
  tag,
  defaultVisible,
  underwriterList,
  label,
  person,
  onUpdate,
  userIsUnderwriter,
  underwriterFromUser,
  userId,
}) => {
  return (
    <Visible byTag={tag} defaultVisible={defaultVisible}>
      <Grid
        container
        item
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        spacing={config.GRID_SPACING}
      >
        {(underwriterList ?? []).length > 0 ? (
          <>
            <Grid item xs={true}>
              <Disabled ifReadOnly>
                <PersonSelect
                  style={{ alignSelf: "flex-start" }}
                  label={label}
                  person={person}
                  personList={underwriterList}
                  onUpdatePerson={onUpdate}
                  onUpdatePersonList={() => {}}
                  showCompany={false}
                  showEmail={false}
                  showLabelOnCard={true}
                />
              </Disabled>
            </Grid>
            {userIsUnderwriter && person?.id !== userId && (
              <Disabled ifReadOnly>
                <Visible
                  byTag={"input.program.insuredDetail.me-underwriter"}
                  defaultVisible={true}
                >
                  <Grid item xs={"auto"}>
                    <Button
                      onClick={() => onUpdate(underwriterFromUser)}
                      variant={"contained"}
                      disableElevation
                      color={"primary"}
                    >
                      {"Me"}
                    </Button>
                  </Grid>
                </Visible>
              </Disabled>
            )}
          </>
        ) : (
          <Grid item xs={12}>
            <TextField
              type={"text"}
              InputLabelProps={{ shrink: true }}
              InputProps={{ spellCheck: "false" }}
              label={label}
              autoComplete={"off"}
              id={"program-preferred-underwriter"}
              variant={"filled"}
              onChange={(e) => onUpdate(e.target.value)}
              value={person?.name ?? ""}
              fullWidth
            />
          </Grid>
        )}
      </Grid>
    </Visible>
  );
};

const NAICSSearch = makeSearch((state) => ({
  searchState: state.search.naics,
  active: state.input.program.naics,
  meta: config.NAICS_SEEKER,
  displayResultText: naicsDisplayResultText,
  renderValue: naicsRenderValue,
  error: programSelectors.isPricingNaicsInError(state),
}));

const CompanyNAICSSearch = makeSearch(
  (state) => ({
    searchState: state.search.companyNaics,
    active: programSelectors.getCompanyNaics(state),
    meta: config.COMPANY_NAICS_SEEKER,
    displayResultText: naicsDisplayResultText,
    renderValue: naicsRenderValue,
  }),
  undefined,
  programActions.clearCompanyNaics
);

const InsuredInput = (props) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  return (
    <Stack direction={"row"} gap={1} alignItems={"center"}>
      <Box sx={{ flexGrow: 1 }}>
        <AISuggestionTextField
          fieldKey={"insured"}
          label={translateLabel(labels.INSURED, {
            default: "Insured",
          })}
          data-testid={"insured"}
          id={props.id}
          onChange={(value) => props.updateInsured(value)}
          value={props.insured}
        />
      </Box>
      {props.isNew && (
        <Visible
          byTag={"input.program.insuredDetail.companySearch"}
          defaultVisible
        >
          <IconButton
            onClick={props.onOpenSearch}
            color={"primary"}
            icon={SearchIcon}
            tooltip={translateLabel(labels.COMPANY_SEARCH_TOOLTIP, {
              default: "Search SEC filings for company name and address",
            })}
          />
        </Visible>
      )}
    </Stack>
  );
};

const selectStableMetadata = createSelector(
  [
    persistenceSelectors.selectIsNew,
    staticDataSelectors.selectUnderwriterFromUser,
    userSelectors.selectUserConfig,
    userSelectors.selectUserAttributes,
    (state) => state.temp?.flags?.categoryCodeVisible || false,
  ],
  (
    isNew,
    underwriterFromUser,
    userConfig,
    userAttributes,
    categoryCodeVisible
  ) => ({
    isNew,
    userIsUnderwriter: underwriterFromUser != null,
    underwriterFromUser,
    userConfig,
    userId: userAttributes?.id,
    categoryCodeVisible,
    showMtaCancellationWarnings:
      userConfig?.componentVisibility?.[
        "input.program.insuredDetail.mtaCancellationWarnings"
      ] ?? false,
  })
);

export const Program = () => {
  const dispatch = useDispatch();
  const [isCompanySearchOpen, setIsCompanySearchOpen] = useState(false);
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { data: transactionTypes } = staticDataService.useConfigQuery(
    "transaction_types"
  );

  const stableMetadata = useSelector(selectStableMetadata);

  // deep data (uses default reference equality)
  const currencyCodesList = useSelector(
    staticDataSelectors.selectCurrencyCodes
  );
  const underwriterList = useSelector(
    staticDataSelectors.selectUnderwriterList
  );
  const layers = useSelector(pricingSelectors.selectResolvedLayers);
  const activeLayer = useSelector(pricingSelectors.selectResolvedActiveLayer);

  const callbacks = useMemo(
    () =>
      bindActionCreators(
        {
          ...programActions,
          updatePreferredUnderwriter,
          updatePreferredUnderwriterName,
          updateSecondaryUnderwriter,
          updateUnderwriterAssistant,
          updateAllLayersReinsurance: pricingActions.updateAllLayersReinsurance,
          updateLayerDefaults: layerDefaultsStore.update,
        },
        dispatch
      ),
    [dispatch]
  );

  const program = useSelector(programSelectors.selectProgram);
  const resolvedProgram = useSelector(programSelectors.selectResolvedProgram);
  const layerDefaults = useSelector(layerDefaultsStore.select);
  const preferredUnderwriter = useSelector(selectPreferredUnderwriter);
  const secondaryUnderwriter = useSelector(selectSecondaryUnderwriter);
  const underwriterAssistant = useSelector(selectUnderwriterAssistant);

  const transactionOptions = useMemo(
    () =>
      makeTransactionOptions(transactionTypes, resolvedProgram?.transaction),
    [transactionTypes, resolvedProgram?.transaction]
  );

  const props = {
    ...stableMetadata,
    ...callbacks,
    ...resolvedProgram, // contains all program fields with computed defaults
    program, // program state before resolution
    currencyCodesList,
    underwriterList,
    layers,
    activeLayer,
    layerDefaults,
    preferredUnderwriter,
    secondaryUnderwriter,
    underwriterAssistant,
  };

  return (
    <>
      {isCompanySearchOpen && (
        <CompanySearch
          open={true}
          onClose={() => setIsCompanySearchOpen(false)}
          onSelect={(company) => {
            props.updateInsured(company.name);
            props.updateRiskDescription(company.description);
            props.updateInsuredAddress(company.address);
            setIsCompanySearchOpen(false);
          }}
        />
      )}
      <Visible byTag={"input.program.riskSummary"} defaultVisible={true}>
        <Grid item xs={12}>
          <WithResearchBadge
            fieldKey={"risk_summary"}
            offsetTop={4}
            offsetRight={4}
          >
            <AIHighlightWrapper fieldKey={"riskSummary"}>
              <Notes
                title={translateLabel(labels.RISK_SUMMARY_TITLE, {
                  default: "Risk Summary",
                })}
                md_width={12}
                update={props.updateRiskSummary}
                value={props.riskSummary}
              />
            </AIHighlightWrapper>
          </WithResearchBadge>
        </Grid>
      </Visible>
      <Visible byTag={"input.program.insuredDetail"} defaultVisible={true}>
        <Grid item md={6} sm={12}>
          <Grid container direction={"column"} spacing={3}>
            <Grid item>
              <Component
                title={translateLabel(labels.INSURED_DETAILS_TITLE, {
                  default: "Insured Details",
                })}
              >
                <Grid container spacing={config.GRID_SPACING}>
                  <Visible
                    byTag={"input.program.insuredDetail.cession"}
                    defaultVisible={false}
                  >
                    <Cession />
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.insured"}
                    defaultVisible={true}
                  >
                    <Grid item xs={12}>
                      <InsuredInput
                        onOpenSearch={() => setIsCompanySearchOpen(true)}
                        id={"program-inputs-insured"}
                        {...props}
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={
                      "input.program.insuredDetail.insuredWithSanctionsSearch"
                    }
                    defaultVisible={false}
                  >
                    <Grid item xs={12} lg={8} xl={9}>
                      <InsuredInput
                        onOpenSearch={() => setIsCompanySearchOpen(true)}
                        {...props}
                      />
                    </Grid>
                    <Disabled ifReadOnly>
                      <Grid item xs={12} lg={4} xl={3}>
                        <FormControlLabel
                          label={translateLabel(
                            labels.IS_SANCTIONS_SEARCH_COMPLETE,
                            {
                              default: "Sanctions Search",
                            }
                          )}
                          control={
                            <Checkbox
                              checked={props.isSanctionsSearchComplete ?? false}
                              onChange={(e) =>
                                props.updateProgram({
                                  isSanctionsSearchComplete:
                                    e?.target?.checked ?? false,
                                })
                              }
                            />
                          }
                          componentsProps={{
                            typography: { variant: "caption" },
                          }}
                          sx={{ height: "100%" }}
                        />
                      </Grid>
                    </Disabled>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.riskDescription"}
                    defaultVisible={true}
                  >
                    <Grid item xs={12}>
                      <WithResearchBadge fieldKey={"risk_description"}>
                        <AISuggestionTextField
                          fieldKey={"riskDescription"}
                          label={translateLabel(labels.RISK_DESCRIPTION, {
                            default: "Risk Description",
                          })}
                          id={"program-inputs-risk-description"}
                          onChange={(value) =>
                            props.updateRiskDescription(value)
                          }
                          value={props.riskDescription}
                        />
                      </WithResearchBadge>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.insuredAddress"}
                    defaultVisible={true}
                  >
                    <Grid item xs={12}>
                      <AISuggestionTextField
                        fieldKey={"insuredAddress"}
                        label={translateLabel(labels.INSURED_ADDRESS, {
                          default: "Insured Address",
                        })}
                        id={"program-insured-address"}
                        onChange={(value) => props.updateInsuredAddress(value)}
                        value={props.insuredAddress}
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={INSURED_ADDRESS_COMPONENTS}
                    defaultVisible={false}
                  >
                    <InsuredAddressComponents
                      insuredAddress={props.insuredAddress}
                      insuredAddressComponents={props.insuredAddressComponents}
                      updateInsuredAddressComponents={
                        props.updateInsuredAddressComponents
                      }
                    />
                  </Visible>
                  <Visible byTag={MAILING_ADDRESS}>
                    <Grid item xs={12}>
                      <AISuggestionTextField
                        fieldKey={"mailingAddress"}
                        label={translateLabel(labels.MAILING_ADDRESS, {
                          default: "Mailing Address",
                        })}
                        onChange={(value) => props.updateMailingAddress(value)}
                        value={props.mailingAddress}
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.insuredLogo"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12}>
                      <InsuredLogo
                        insuredLogo={props.insuredLogo}
                        updateProgram={props.updateProgram}
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.inception"}
                    defaultVisible={true}
                  >
                    <Grid item xs={4}>
                      <AIHighlightWrapper fieldKey={"inception"}>
                        <FormattedDatePicker
                          label={translateLabel(labels.INCEPTION, {
                            default: "Inception",
                          })}
                          variant={"filled"}
                          onChangeDate={props.updateInception}
                          value={props.inception ?? ""}
                          fullWidth
                          clearable={false}
                          helperText={
                            props.showMtaCancellationWarnings &&
                            (props.transaction === "MTA" ||
                              props.transaction === "CANCELLATION")
                              ? "Do not change from originally bound policy"
                              : undefined
                          }
                        />
                      </AIHighlightWrapper>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.expiration"}
                    defaultVisible={true}
                  >
                    <Grid item xs={4}>
                      <AIHighlightWrapper fieldKey={"expiration"}>
                        <FormattedDatePicker
                          label={translateLabel(labels.EXPIRATION, {
                            default: "Expiration",
                          })}
                          onChangeDate={props.updateExpiration}
                          value={props.expiration ?? ""}
                          fullWidth
                          clearable={false}
                          helperText={
                            props.showMtaCancellationWarnings &&
                            (props.transaction === "MTA" ||
                              props.transaction === "CANCELLATION")
                              ? "Do not change from originally bound policy"
                              : undefined
                          }
                        />
                      </AIHighlightWrapper>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.proRata"}
                    defaultVisible={true}
                  >
                    <Grid item xs={4}>
                      <PrettyNumberTextField
                        label={translateLabel(labels.PRO_RATA, {
                          default: "Pro Rata",
                        })}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ spellCheck: "false" }}
                        autoComplete={"off"}
                        variant={"percentage"}
                        onChangeNumber={props.updateProRataAdjustment}
                        value={props.proRataAdjustment ?? null}
                        fullWidth
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.nonRenewing"}
                    defaultVisible
                  >
                    <Grid item xs={12}>
                      <Disabled ifReadOnly>
                        <FormControlLabel
                          label={translateLabel(labels.PROGRAM_NON_RENEWING, {
                            default: "Non-renewing (e.g. one-off project)",
                          })}
                          control={
                            <Checkbox
                              checked={props.isNonRenewing ?? false}
                              onChange={(e) =>
                                props.updateProgram({
                                  isNonRenewing: e?.target?.checked ?? false,
                                })
                              }
                            />
                          }
                        />
                      </Disabled>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.multiYearDeal"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12}>
                      <Disabled ifReadOnly>
                        <FormControlLabel
                          label={translateLabel(
                            labels.PROGRAM_MULTI_YEAR_DEAL,
                            {
                              default: "Multi-year deal",
                            }
                          )}
                          control={
                            <Checkbox
                              checked={props.isMultiYear ?? false}
                              onChange={(e) =>
                                e?.target?.checked
                                  ? props.updateProgram({
                                      isMultiYear: true,
                                    })
                                  : props.updateProgram({
                                      isMultiYear: false,
                                      cancelAtFirstAnniversary: false,
                                      // a recorded cancellation date (before
                                      // expiration) is cleared so the full
                                      // term stands; an extension (after
                                      // expiration) is kept
                                      ...(props.cancelAtFirstAnniversary &&
                                      props.program?.effectiveExpiration &&
                                      props.program.effectiveExpiration <
                                        props.expiration
                                        ? { effectiveExpiration: null }
                                        : {}),
                                    })
                              }
                            />
                          }
                        />
                      </Disabled>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={
                      "input.program.insuredDetail.cancelAtFirstAnniversary"
                    }
                    defaultVisible={false}
                  >
                    {props.isMultiYear ? (
                      <Grid item xs={12}>
                        <Disabled ifReadOnly>
                          <FormControlLabel
                            label={translateLabel(
                              labels.PROGRAM_CANCEL_AT_FIRST_ANNIVERSARY,
                              {
                                default:
                                  "Cancel and rewrite at first anniversary",
                              }
                            )}
                            control={
                              <Checkbox
                                checked={
                                  props.cancelAtFirstAnniversary ?? false
                                }
                                onChange={(e) =>
                                  e?.target?.checked
                                    ? props.updateProgram({
                                        cancelAtFirstAnniversary: true,
                                      })
                                    : props.updateProgram({
                                        cancelAtFirstAnniversary: false,
                                        // unticking means the guaranteed
                                        // renewal stands: a recorded
                                        // cancellation date (before
                                        // expiration) is cleared so the
                                        // full term is restored; an
                                        // extension (after expiration) is
                                        // kept
                                        ...(props.program
                                          ?.effectiveExpiration &&
                                        props.program.effectiveExpiration <
                                          props.expiration
                                          ? { effectiveExpiration: null }
                                          : {}),
                                      })
                                }
                              />
                            }
                          />
                        </Disabled>
                      </Grid>
                    ) : (
                      <></>
                    )}
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.auditable"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12}>
                      <Disabled ifReadOnly>
                        <FormControlLabel
                          label={translateLabel(labels.PROGRAM_AUDITABLE, {
                            default: "Auditable",
                          })}
                          control={
                            <Checkbox
                              checked={props.auditable ?? false}
                              onChange={(e) =>
                                props.updateProgram({
                                  auditable: e?.target?.checked ?? false,
                                })
                              }
                            />
                          }
                        />
                      </Disabled>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.formOfBusiness"}
                    defaultVisible={false}
                  >
                    <FormOfBusiness
                      formOfBusiness={props.formOfBusiness}
                      updateProgram={props.updateProgram}
                    />
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.transaction"}
                    defaultVisible={false}
                  >
                    <Grid item xs={6}>
                      <Disabled ifReadOnly>
                        <FormControl variant={"filled"} fullWidth>
                          <InputLabel shrink={true}>
                            {translateLabel(labels.TRANSACTION, {
                              default: "Transaction",
                            })}
                          </InputLabel>
                          <Select
                            value={props.transaction}
                            onChange={(event) =>
                              props.updateProgram({
                                transaction: event.target.value,
                              })
                            }
                          >
                            {transactionOptions.map((option) => (
                              <MenuItem key={option.key} value={option.key}>
                                {translateLabel(option.label, {
                                  default: option.name,
                                })}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Disabled>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.effectiveDate"}
                    defaultVisible={false}
                  >
                    <Grid item xs={6}>
                      <FormattedDatePicker
                        label={translateLabel(labels.EFFECTIVE_DATE, {
                          default: "Effective Date",
                        })}
                        onChangeDate={(effectiveDate) =>
                          props.updateProgram({ effectiveDate })
                        }
                        value={props.effectiveDate ?? ""}
                        fullWidth
                        clearable={false}
                        helperText={
                          props.showMtaCancellationWarnings &&
                          (props.transaction === "MTA" ||
                            props.transaction === "CANCELLATION")
                            ? "✓ Use this field for transaction effective date"
                            : undefined
                        }
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.effectiveExpiration"}
                    defaultVisible={false}
                  >
                    <Grid item xs={6}>
                      <FormattedDatePicker
                        label={translateLabel(labels.EFFECTIVE_EXPIRATION, {
                          default: "Effective Expiration",
                        })}
                        onChangeDate={(effectiveExpiration) =>
                          props.updateProgram({ effectiveExpiration })
                        }
                        value={props.effectiveExpiration ?? ""}
                        fullWidth
                        clearable={true}
                        helperText={
                          props.cancelAtFirstAnniversary
                            ? "Set to the first anniversary when years 2–3 are cancelled and rewritten"
                            : "Use when policy period is extended beyond original expiration"
                        }
                      />
                      {props.cancelAtFirstAnniversary &&
                        props.program?.inception &&
                        !props.program?.effectiveExpiration && (
                          <Disabled ifReadOnly>
                            <Button
                              size={"small"}
                              onClick={() =>
                                props.updateProgram({
                                  effectiveExpiration: dates.getMoveForwardOneYear(
                                    props.program.inception
                                  ),
                                })
                              }
                            >
                              {"Cancel years 2–3 at first anniversary"}
                            </Button>
                          </Disabled>
                        )}
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.reference"}
                    defaultVisible={true}
                  >
                    <Grid item xs={12}>
                      <AIHighlightWrapper fieldKey={"reference"}>
                        <TextField
                          type={"text"}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ spellCheck: "false" }}
                          label={translateLabel(labels.PROGRAM_REFERENCE, {
                            default: "Reference",
                          })}
                          autoComplete={"off"}
                          variant={"filled"}
                          onChange={(e) =>
                            props.updateReference(e.target.value)
                          }
                          value={props.reference}
                          fullWidth
                        />
                      </AIHighlightWrapper>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.policySummary"}
                    defaultVisible={false}
                  >
                    <PolicySummary layers={props.layers} />
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.quoteReference"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12}>
                      <TextField
                        type={"text"}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ spellCheck: "false" }}
                        label={translateLabel(labels.QUOTE_REFERENCE, {
                          default: "Quote Reference",
                        })}
                        autoComplete={"off"}
                        variant={"filled"}
                        onChange={(e) =>
                          props.updateProgram({
                            quoteReference: e.target.value,
                          })
                        }
                        value={props.quoteReference ?? ""}
                        fullWidth
                      />
                    </Grid>
                  </Visible>
                  <UnderwriterSelect
                    tag={"input.program.insuredDetail.preferredUnderwriter"}
                    defaultVisible={true}
                    underwriterList={props.underwriterList}
                    label={translateLabel(labels.PREFERRED_UNDERWRITER, {
                      default: "Preferred Underwriter",
                    })}
                    person={props.preferredUnderwriter}
                    onUpdate={props.updatePreferredUnderwriter}
                    userIsUnderwriter={props.userIsUnderwriter}
                    underwriterFromUser={props.underwriterFromUser}
                    userId={props.userId}
                  />
                  <UnderwriterSelect
                    tag={"input.program.insuredDetail.secondaryUnderwriter"}
                    defaultVisible={false}
                    underwriterList={props.underwriterList}
                    label={translateLabel(labels.SECONDARY_UNDERWRITER, {
                      default: "Secondary Underwriter",
                    })}
                    person={props.secondaryUnderwriter}
                    onUpdate={props.updateSecondaryUnderwriter}
                    userIsUnderwriter={props.userIsUnderwriter}
                    underwriterFromUser={props.underwriterFromUser}
                    userId={props.userId}
                  />
                  <UnderwriterSelect
                    tag={PROGRAM_UNDERWRITER_ASSISTANT}
                    defaultVisible={false}
                    underwriterList={props.underwriterList}
                    label={translateLabel(labels.UNDERWRITER_ASSISTANT, {
                      default: "Underwriter Assistant",
                    })}
                    person={props.underwriterAssistant}
                    onUpdate={props.updateUnderwriterAssistant}
                    userIsUnderwriter={props.userIsUnderwriter}
                    underwriterFromUser={props.underwriterFromUser}
                    userId={props.userId}
                  />
                  <Visible
                    byTag={"input.program.insuredDetail.priority"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12}>
                      <Disabled ifReadOnly>
                        <FormControl variant={"filled"} fullWidth>
                          <InputLabel shrink={true}>
                            {translateLabel(labels.PRIORITY, {
                              default: "Priority",
                            })}
                          </InputLabel>
                          <Select
                            value={props.priority ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              props.updateProgram({
                                priority: value === "" ? null : value,
                              });
                            }}
                          >
                            <MenuItem value={""}>{"None"}</MenuItem>
                            <Divider />
                            <MenuItem value={1}>{"High"}</MenuItem>
                            <MenuItem value={2}>{"Medium"}</MenuItem>
                            <MenuItem value={3}>{"Low"}</MenuItem>
                          </Select>
                        </FormControl>
                      </Disabled>
                    </Grid>
                  </Visible>
                  <Grid item container xs={12} spacing={config.GRID_SPACING}>
                    <Visible
                      byTag={"input.program.insuredDetail.dateReceived"}
                      defaultVisible={true}
                    >
                      <Grid item xs={4}>
                        <FormattedDatePicker
                          label={translateLabel(labels.DATE_RECEIVED, {
                            default: "Date Received",
                          })}
                          onChangeDate={(dateReceived) =>
                            props.updateProgram({ dateReceived })
                          }
                          value={props.dateReceived ?? ""}
                          fullWidth
                          clearable={false}
                        />
                      </Grid>
                    </Visible>
                    <Visible
                      byTag={"input.program.insuredDetail.quoteDeadline"}
                      defaultVisible={true}
                    >
                      <Grid item xs={4}>
                        <FormattedDatePicker
                          label={translateLabel(labels.QUOTE_DEADLINE, {
                            default: "Quote Deadline",
                          })}
                          onChangeDate={(quoteDeadline) =>
                            props.updateProgram({ quoteDeadline })
                          }
                          value={props.quoteDeadline ?? ""}
                          fullWidth
                          clearable={false}
                        />
                      </Grid>
                    </Visible>
                    <Visible
                      byTag={"input.program.insuredDetail.quoteDate"}
                      defaultVisible={true}
                    >
                      <Grid item xs={4}>
                        <FormattedDatePicker
                          label={translateLabel(labels.QUOTE_DATE, {
                            default: "Quote Date",
                          })}
                          onChangeDate={(quoteDate) =>
                            props.updateProgram({ quoteDate })
                          }
                          value={props.quoteDate ?? ""}
                          fullWidth
                          clearable={false}
                        />
                      </Grid>
                    </Visible>
                    <Visible
                      byTag={"input.program.insuredDetail.quoteValidUntil"}
                      defaultVisible={true}
                    >
                      <Grid item xs={4}>
                        <FormattedDatePicker
                          label={translateLabel(labels.QUOTE_VALID_UNTIL, {
                            default: "Quote Valid Until",
                          })}
                          onChangeDate={(quoteValidUntil) =>
                            props.updateProgram({ quoteValidUntil })
                          }
                          value={props.quoteValidUntil ?? ""}
                          fullWidth
                          clearable={false}
                        />
                      </Grid>
                    </Visible>
                    <Visible
                      byTag={"input.program.insuredDetail.warrantyDate"}
                      defaultVisible={false}
                    >
                      <Grid item xs={4}>
                        <FormattedDatePicker
                          label={translateLabel(labels.WARRANTY_DATE, {
                            default: "Warranty Date",
                          })}
                          onChangeDate={(warrantyDate) =>
                            props.updateProgram({ warrantyDate })
                          }
                          value={props.warrantyDate ?? ""}
                          fullWidth
                          clearable={false}
                        />
                      </Grid>
                    </Visible>
                    <Toggle
                      byTag={"input.program.statusBlock"}
                      trueChild={<></>}
                      falseChild={
                        <Visible
                          byTag={"input.program.insuredDetail.status"}
                          defaultVisible={false}
                        >
                          <Grid item xs={4}>
                            <StatusOverrideSelector
                              program={props.program}
                              updateProgram={props.updateProgram}
                            />
                          </Grid>
                        </Visible>
                      }
                    />
                  </Grid>
                  <Visible
                    byTag={"input.program.insuredDetail.brokerage"}
                    defaultVisible={true}
                  >
                    <Grid item xs={12} md={6}>
                      <AIHighlightWrapper fieldKey={"brokerage"}>
                        <PrettyNumberTextField
                          type={"text"}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ spellCheck: "false" }}
                          label={translateLabel(labels.PROGRAM_BROKERAGE, {
                            default: "Brokerage",
                          })}
                          autoComplete={"off"}
                          id={"program-inputs-brokerage"}
                          variant={"percentage"}
                          onChangeNumber={props.updateBrokerage}
                          value={props.brokerage}
                          fullWidth
                        />
                      </AIHighlightWrapper>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.requestedBrokerage"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12} md={6}>
                      <PrettyNumberTextField
                        type={"text"}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ spellCheck: "false" }}
                        label={translateLabel(
                          labels.PROGRAM_REQUESTED_BROKERAGE,
                          {
                            default: "Requested Brokerage",
                          }
                        )}
                        autoComplete={"off"}
                        id={"program-inputs-requested-brokerage"}
                        variant={"percentage"}
                        onChangeNumber={props.updateRequestedBrokerage}
                        value={props.requestedBrokerage}
                        fullWidth
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.oac"}
                    defaultVisible={true}
                  >
                    <Tooltip title={"Other Acquisition Costs %"}>
                      <Grid item xs={12} md={6}>
                        <PrettyNumberTextField
                          type={"text"}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ spellCheck: "false" }}
                          label={translateLabel(labels.PROGRAM_OAC, {
                            default: "OAC",
                          })}
                          autoComplete={"off"}
                          id={"program-inputs-other-acquisition-costs"}
                          variant={"percentage"}
                          onChangeNumber={props.updateOtherAcquisitionCosts}
                          value={props.otherAcquisitionCosts}
                          fullWidth
                        />
                      </Grid>
                    </Tooltip>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.targetProfit"}
                    defaultVisible={true}
                  >
                    <Grid item xs={12} md={6}>
                      <AIHighlightWrapper fieldKey={"profit"}>
                        <PrettyNumberTextField
                          type={"text"}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ spellCheck: "false" }}
                          label={translateLabel(labels.PROGRAM_TARGET_PROFIT, {
                            default: "Target Profit",
                          })}
                          autoComplete={"off"}
                          id={"program-inputs-profit"}
                          variant={"percentage"}
                          onChangeNumber={props.updateProfit}
                          value={props.profit}
                          fullWidth
                        />
                      </AIHighlightWrapper>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.gnulr"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12} md={6}>
                      <Disabled
                        byTag={"input.program.insuredDetail.gnulr"}
                        defaultDisabledByTag={false}
                      >
                        <PrettyNumberTextField
                          type={"text"}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ spellCheck: "false" }}
                          label={translateLabel(labels.PROGRAM_GNULR, {
                            default: "GNULR",
                          })}
                          autoComplete={"off"}
                          id={"program-inputs-gnulr"}
                          variant={"percentage"}
                          onChangeNumber={(value) => {
                            props.updateProfit(100 - value);
                          }}
                          value={100 - props.profit}
                          fullWidth
                        />
                      </Disabled>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.expenses"}
                    defaultVisible={true}
                  >
                    <Grid item xs={12} md={6}>
                      <PrettyNumberTextField
                        type={"text"}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ spellCheck: "false" }}
                        label={translateLabel(labels.PROGRAM_EXPENSES, {
                          default: "Expenses",
                        })}
                        autoComplete={"off"}
                        id={"program-inputs-expenses"}
                        variant={"percentage"}
                        onChangeNumber={props.updateExpenses}
                        value={props.expenses}
                        fullWidth
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.policyFee"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12} md={6}>
                      <PrettyNumberTextField
                        type={"text"}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ spellCheck: "false" }}
                        label={translateLabel(labels.PROGRAM_POLICY_FEE, {
                          default: "Policy Fee",
                        })}
                        autoComplete={"off"}
                        id={"program-inputs-policy-fee"}
                        variant={"filled"}
                        onChangeNumber={(value) =>
                          props.updateProgram({ policyFee: value })
                        }
                        value={props.policyFee ?? null}
                        fullWidth
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.inspectionFee"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12} md={6}>
                      <PrettyNumberTextField
                        type={"text"}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ spellCheck: "false" }}
                        label={translateLabel(labels.PROGRAM_INSPECTION_FEE, {
                          default: "Inspection Fee",
                        })}
                        autoComplete={"off"}
                        id={"program-inputs-inspection-fee"}
                        variant={"filled"}
                        onChangeNumber={(value) =>
                          props.updateProgram({ inspectionFee: value })
                        }
                        value={props.inspectionFee ?? null}
                        fullWidth
                      />
                    </Grid>
                  </Visible>
                  <Visible byTag={"input.program.insuredDetail.paymentTerms"}>
                    <PaymentTermsForm
                      paymentTerms={props.paymentTerms}
                      setPaymentTerms={props.updatePaymentTerms}
                    />
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.naicsForPricing"}
                    defaultVisible={true}
                  >
                    <Grid item xs={6}>
                      <AIHighlightWrapper fieldKey={"naics"}>
                        <NAICSSearch
                          label={translateLabel(labels.PRICING_NAICS, {
                            default: "NAICS for Pricing",
                          })}
                          data-testid={"NAICS-search"}
                        />
                      </AIHighlightWrapper>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.naicsOfCompany"}
                    defaultVisible={true}
                  >
                    <Grid item xs={6}>
                      <CompanyNAICSSearch
                        label={translateLabel(labels.COMPANY_NAICS, {
                          default: "NAICS of Company",
                        })}
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.inUsLocation"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12} sm={5}>
                      <FormControl component={"fieldset"} variant={"standard"}>
                        <FormLabel
                          component={"legend"}
                          className={"input-label"}
                        >
                          {translateLabel(labels.LOCATION, {
                            default: "Location",
                          })}
                        </FormLabel>
                        <RadioGroup
                          row
                          aria-label={"in-us"}
                          id={"program-inputs-in-us"}
                          name={"in-us"}
                          value={props.inUS ? US : NON_US}
                          onChange={(event) => {
                            props.updateWhetherInUS(event.target.value === US);
                          }}
                        >
                          <FormControlLabel
                            value={US}
                            control={<Radio color={"primary"} />}
                            label={"US"}
                          />
                          <FormControlLabel
                            value={NON_US}
                            control={<Radio color={"primary"} />}
                            label={"Non-US"}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.turnoverCategory"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12} sm={7}>
                      <Disabled ifReadOnly>
                        <FormControl variant={"filled"} fullWidth>
                          <InputLabel className={"input-label"}>
                            {translateLabel(labels.TURNOVER, {
                              default: "Turnover",
                            })}
                          </InputLabel>
                          <Select
                            value={props.turnover}
                            onChange={(event) =>
                              props.updateTurnover(event.target.value)
                            }
                            inputProps={{
                              name: "program-inputs-turnover",
                              id: "program-inputs-turnover",
                            }}
                            name={"turnover"}
                            className={"program-inputs-turnover"}
                          >
                            {config.TURNOVERS.map((turnover) => {
                              return (
                                <MenuItem
                                  key={`Turnover_Item_${turnover.key}`}
                                  value={turnover.key}
                                >
                                  {turnover.value}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Disabled>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.currency"}
                    defaultVisible={true}
                  >
                    <Grid item xs={4}>
                      <AIHighlightWrapper fieldKey={"currency"}>
                        <Disabled ifReadOnly>
                          <Autocomplete
                            options={props.currencyCodesList}
                            renderInput={(params) => (
                              <TextField
                                label={translateLabel(labels.CURRENCY, {
                                  default: "Currency",
                                })}
                                variant={"filled"}
                                fullWidth
                                {...params}
                              />
                            )}
                            disableClearable
                            autoHighlight
                            onChange={(event, value, reason) => {
                              if (reason === "selectOption") {
                                props.updateCurrency(value);
                              }
                            }}
                            value={props.currency}
                          />
                        </Disabled>
                      </AIHighlightWrapper>
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.reassured"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12}>
                      <TextField
                        type={"text"}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ spellCheck: "false" }}
                        label={translateLabel(labels.REASSURED, {
                          default: "Reassured",
                        })}
                        autoComplete={"off"}
                        variant={"filled"}
                        onChange={(e) => {
                          let value = e.target.value;
                          if (!value || value === "") {
                            value = null;
                          }
                          props.updateReassured(value);
                        }}
                        value={props.reassured ?? ""}
                        fullWidth
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.reinsurance"}
                    defaultVisible={false}
                  >
                    <ReinsuranceAggregator
                      label={translateLabel(labels.DEFAULT_REINSURANCE, {
                        default: "Security",
                      })}
                      layers={props.layers}
                      onChange={props.updateAllLayersReinsurance}
                    />
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.defaultPaperProvider"}
                    defaultVisible={false}
                  >
                    <ReinsuranceSelector
                      label={translateLabel(labels.DEFAULT_PAPER_PROVIDER, {
                        default: "Default Paper Provider",
                      })}
                      value={props.layerDefaults?.paperProvider ?? null}
                      onChange={(paperProvider) => {
                        props.updateLayerDefaults({ paperProvider });
                      }}
                    />
                  </Visible>
                  <Visible
                    byTag={"input.program.stateOfFiling"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12}>
                      <StateOfFiling
                        value={props.stateOfFiling}
                        onChange={props.updateStateOfFiling}
                      />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.surplusBroker"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12}>
                      <SurplusBroker />
                    </Grid>
                  </Visible>
                  <Visible
                    byTag={"input.program.insuredDetail.njTransactionNumber"}
                    defaultVisible={false}
                  >
                    <Grid item xs={12}>
                      <NjTransactionNumber />
                    </Grid>
                  </Visible>
                </Grid>
              </Component>
            </Grid>
          </Grid>
        </Grid>
      </Visible>
      <Grid item md={6} sm={12}>
        <Grid container direction={"column"} spacing={3}>
          <Visible byTag={"input.program.statusBlock"} defaultVisible={false}>
            <ProgramStatus />
          </Visible>
          {((props.userConfig?.componentVisibility?.[
            "input.program.industryClass"
          ] ??
            true) ||
            (props.userConfig?.componentVisibility?.[
              "input.program.modelSelector"
            ] ??
              true) ||
            (props.userConfig?.componentVisibility?.[
              "input.program.categoryCode"
            ] ??
              true)) && (
            <Grid item>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={config.GRID_SPACING}
              >
                <Visible
                  byTag={"input.program.industryClass"}
                  defaultVisible={true}
                >
                  <IndustryClass />
                </Visible>
                <Visible
                  byTag={"input.program.modelSelector"}
                  defaultVisible={false}
                >
                  <ModelSelector />
                </Visible>
                <Visible
                  byTag={"input.program.categoryCode"}
                  defaultVisible={true}
                >
                  <AIHighlightWrapper fieldKey={"categoryCode"}>
                    {props.categoryCodeVisible ? <CategoryCode /> : <></>}
                  </AIHighlightWrapper>
                </Visible>
              </Stack>
            </Grid>
          )}
          <Visible byTag={"input.program.companySic"} defaultVisible={false}>
            <SICCodeSearch />
          </Visible>
          <Visible byTag={"input.program.productLine"} defaultVisible={false}>
            <ProductLine />
          </Visible>
          <Visible byTag={"input.program.tags"} defaultVisible={false}>
            <SubmissionTags />
          </Visible>
          <Visible byTag={"input.program.broker"} defaultVisible={true}>
            <Broker />
          </Visible>
          <Visible byTag={"input.program.assistant"} defaultVisible={false}>
            <Assistant />
          </Visible>
          <ConfiguredPeopleFields />
          <Visible byTag={"input.program.regulatoryExemptions"}>
            <Grid item xs={6}>
              <RegulatoryExemptions
                program={props.program}
                updateProgram={props.updateProgram}
              />
            </Grid>
          </Visible>
          <Visible
            byTag={"input.program.primaryExposure"}
            defaultVisible={true}
          >
            <PrimaryExposure fullWidth={true} borderColour={"transparent"} />
          </Visible>
          <Visible
            byTag={"input.program.standaloneExposure"}
            defaultVisible={false}
          >
            <StandaloneExposure />
          </Visible>
          <Visible byTag={"input.program.duns"} defaultVisible={false}>
            <Duns />
          </Visible>
          <HiddenIfSnapshot>
            <Visible byTag={"input.program.share"} defaultVisible={false}>
              <Share />
            </Visible>
          </HiddenIfSnapshot>
          <Visible byTag={"input.program.renewal"} defaultVisible={true}>
            <Renewal />
          </Visible>
          <Visible byTag={"input.program.programTiv"} defaultVisible={false}>
            <ProgramTiv />
          </Visible>
          <Visible byTag={"input.program.paymentTerms"} defaultVisible={false}>
            <ProgramPaymentTerms />
          </Visible>
          <Visible byTag={"input.program.datascore"} defaultVisible={false}>
            <Grid item>
              <Component title={"Data Quality"}>
                <Disabled ifReadOnly>
                  <DataScore
                    initialValue={props.dataQuality}
                    variant={"select"}
                    onChange={props.updateDataQuality}
                  />
                </Disabled>
              </Component>
            </Grid>
          </Visible>
          <Visible byTag={"input.program.links"} defaultVisible={true}>
            <Links />
          </Visible>
        </Grid>
      </Grid>
      <Visible byTag={"input.program.tower"} defaultVisible={false}>
        <Grid item xs={12}>
          <Tower />
        </Grid>
      </Visible>
      <Visible
        byTag={"input.program.selfInsuredRetentions"}
        defaultVisible={false}
      >
        <Grid item xs={12}>
          <SelfInsuredRetentions />
        </Grid>
      </Visible>
      <Visible byTag={"input.program.layerInput"}>
        <Grid item xs={12}>
          <ActiveLayerSelector
            includedColumnKeys={[
              "delete",
              "status",
              "copyId",
              "security",
              "shareOfLimit",
              "limit",
              "attachment",
              "grossPremium",
              "shareOfPremium",
              "grossPPM",
              "shareOfNetPremium",
              "tac",
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ActiveLayerEditor />
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack
            direction={"column"}
            spacing={config.GRID_SPACING}
            justifyContent={"flex-start"}
          >
            <ActiveLayerPremiumAdjustmentsEditor />
            <Visible
              byTag={"input.program.documentsIssued"}
              defaultVisible={true}
            >
              <DocumentsIssued layer={props.activeLayer} />
            </Visible>
          </Stack>
        </Grid>
      </Visible>
      <Visible byTag={"input.program.notes"} defaultVisible={true}>
        <Grid item xs={12}>
          <AIHighlightWrapper fieldKey={"notes"}>
            <Grid container>
              <Notes
                title={"Program Notes"}
                update={props.updateNotes}
                value={props.notes}
              />
            </Grid>
          </AIHighlightWrapper>
        </Grid>
      </Visible>
    </>
  );
};

export default Program;
